import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject as RxSubject, debounceTime, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { RequestListSearchDialogComponent } from '../request-list-search-dialog/request-list-search-dialog.component';
import { RequestService } from 'src/app/services/request.service';

export interface RequestSearchBarResult {
  data: any[];
  total: number;
  filters: any;
  keyword: string;
}

@Component({
  selector: 'app-request-list-search-bar',
  templateUrl: './request-list-search-bar.component.html',
})
export class RequestListSearchBarComponent implements OnInit, OnDestroy {

  @Input() initialKeyword: string = '';
  @Input() initialFilters: any = null;

  @Output() searchResult = new EventEmitter<RequestSearchBarResult>();
  @Output() cleared = new EventEmitter<void>();
  @Output() searchError = new EventEmitter<string>();

  kwSearch: string = '';
  isSearching = false;
  lastDialogFilters: any = null;

  private searchInput$ = new RxSubject<string>();
  private destroy$ = new RxSubject<void>();

  constructor(
    private dialog: MatDialog,
    private requestService: RequestService,
  ) {}

  ngOnInit(): void {
    this.kwSearch = this.initialKeyword ?? '';
    this.lastDialogFilters = this.initialFilters ?? null;

    this.searchInput$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(keyword => {
        if (!keyword || keyword.trim().length === 0) {
          return of(null);
        }
        this.isSearching = true;
        return this.requestService.extendedSearch({ text: keyword.trim() });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (results) => {
        this.isSearching = false;
        if (results === null) {
          this.cleared.emit();
        } else {
          this.searchResult.emit({
            data: results,
            total: results.length,
            filters: null,
            keyword: this.kwSearch,
          });
        }
      },
      error: (err) => {
        this.isSearching = false;
        this.searchError.emit(err.message);
      }
    });

    if (this.kwSearch) {
      this.searchInput$.next(this.kwSearch);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeFiltersCount(): number {
    if (!this.lastDialogFilters) return 0;
    let count = 0;
    const countValues = (obj: any) => {
      if (obj === null || obj === undefined) return;
      if (Array.isArray(obj)) {
        if (obj.length > 0) count++;
      } else if (typeof obj === 'object') {
        Object.values(obj).forEach(v => countValues(v));
      } else if (obj !== '') {
        count++;
      }
    };
    countValues(this.lastDialogFilters);
    return count;
  }

  onKeywordChange(value: string): void {
    this.kwSearch = value;
    this.lastDialogFilters = null;
    this.searchInput$.next(value);
  }

  clearSearch(): void {
    this.kwSearch = '';
    this.lastDialogFilters = null;
    this.cleared.emit();
  }

  resetDialogFilters(): void {
    this.lastDialogFilters = null;
    this.kwSearch = '';
    this.cleared.emit();
  }

  openAdvancedSearchDialog(): void {
    const ref = this.dialog.open(RequestListSearchDialogComponent, {
      width: '480px',
      data: this.lastDialogFilters ?? { text: this.kwSearch ?? '' }
    });
    ref.afterClosed().subscribe(filters => {
      if (filters === null || filters === undefined) return;
      this.lastDialogFilters = filters;
      this.kwSearch = filters.text ?? '';
      this.isSearching = true;
      const { rawSelections, ...serverFilters } = filters;
      this.requestService.extendedSearch(serverFilters).subscribe({
        next: (results) => {
          this.isSearching = false;
          this.searchResult.emit({
            data: results,
            total: results.length,
            filters: filters,
            keyword: this.kwSearch,
          });
        },
        error: (err) => {
          this.isSearching = false;
          this.searchError.emit(err.message);
        }
      });
    });
  }
}
